import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, FileText, Check, Clock, AlertTriangle, User, Phone, Mail,
  CheckCircle, XCircle, Edit, Save, Calculator, DollarSign, Shield,
  FileCheck, Send, Package, Download, ChevronRight, Plus, Trash2,
  Eye, EyeOff, ArrowDownToLine
} from 'lucide-react';
import {
  RecommendationForm, ReplacementForm,
  AMLKYCForm, UnderwritingNotesForm, PolicyDeliveryForm, SupervisorReviewForm,
  SignatureRequestsForm,
  ElectronicConsentForm, ConflictOfInterestForm, AdvisorNotesForm, ReasonWhyLetterForm
} from './ComplianceForms';
import { CoverageTakenForm, ExistingPoliciesSubTable } from './forms/CoverageTakenForm';
import { InvestorProfileForm } from './forms/InvestorProfileForm';
import { useAutosave } from '../../hooks/useAutosave';
import { ClientIntakePanel } from './forms/ClientIntakePanel';
import { UploadedDocumentsPanel } from './forms/UploadedDocumentsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// =====================================================================
// Default form selection per transaction type
// =====================================================================
// Per user request (iter 40): NO forms are pre-selected on a new case.
// The agent (or admin) explicitly toggles the eye icon on each tab to
// include that section in the final signed compliance PDF. This prevents
// accidental inclusion of irrelevant sections in the generated package.
const DEFAULT_INCLUDED_BY_TXN = {
  new_coverage: [],
  replacement: [],
  upgrade: [],
  downgrade: [],
  cancel: [],
  other: [],
};

/**
 * Renders a single tab button with an eye toggle for "included in package".
 *  - Tabs with key starting `__` are control tabs (overview / signatures) and
 *    don't show the eye toggle.
 *  - Clicking the tab body sets it as the active section.
 *  - Clicking the eye toggles inclusion in the final signed PDF package.
 */
const FormTab = ({ tab, activeSection, setActiveSection, includedForms, onToggleInclude, pageBreakBefore, onTogglePageBreak, vertical = false }) => {
  const isControl = !tab.key || tab.key.startsWith('__');
  // Iter 52 — `advisor_notes` is a mandatory stage and is always included
  // in the signed package; lock its eye toggle so it cannot be unchecked.
  const isMandatory = tab.key === 'advisor_notes';
  const included = isControl ? null : (isMandatory ? true : includedForms.includes(tab.key));
  const pageBreak = !isControl && (pageBreakBefore || []).includes(tab.key);
  const active = activeSection === tab.id;
  return (
    <div
      className={`${vertical ? 'flex w-full' : 'inline-flex'} items-center rounded overflow-hidden ${active ? 'bg-[#0077B6] text-white' : 'bg-transparent text-gray-600 hover:bg-gray-100'}`}
    >
      {!isControl ? (
        <label
          onClick={(e) => e.stopPropagation()}
          className={`pl-2 flex items-center ${isMandatory ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          title={
            isMandatory
              ? 'Advisor Notes is mandatory and always included in PDF generation & e-signing'
              : (included ? 'Included in PDF generation & e-signing — uncheck to exclude' : 'Excluded — check to include in PDF generation & e-signing')
          }
        >
          <input
            type="checkbox"
            checked={!!included}
            disabled={isMandatory}
            onChange={() => { if (!isMandatory) onToggleInclude(tab.key); }}
            data-testid={`tab-include-${tab.id}`}
            className={`h-4 w-4 rounded cursor-pointer ${active ? 'accent-white' : 'accent-[#0077B6]'} ${isMandatory ? 'opacity-70 cursor-not-allowed' : ''}`}
          />
        </label>
      ) : (
        <span className="pl-2 w-4" aria-hidden="true" />
      )}
      <button
        onClick={() => setActiveSection(tab.id)}
        className={`px-2 py-1.5 text-sm font-medium ${vertical ? 'flex-1 text-left' : 'whitespace-nowrap'}`}
        data-testid={`tab-${tab.id}`}
      >
        {tab.label}
        {isMandatory && (
          <span className="ml-1 text-[9px] uppercase font-bold tracking-wide opacity-70">required</span>
        )}
      </button>
      {!isControl && included && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTogglePageBreak(tab.key); }}
          className={`px-1.5 py-1.5 ${active ? 'hover:bg-white/15' : 'hover:bg-gray-200'} ${pageBreak ? '' : 'opacity-40'}`}
          title={pageBreak
            ? 'This section will start on a new PDF page — click to flow continuously'
            : 'This section flows continuously — click to start it on a new PDF page'}
          data-testid={`tab-pagebreak-${tab.id}`}
        >
          <ArrowDownToLine className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

const CaseDetail = ({ agent, caseId, onNavigate, isAdmin = false }) => {
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [saving, setSaving] = useState(false);

  const fetchCaseData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/compliance/cases/${caseId}`);
      if (response.ok) {
        const data = await response.json();
        setCaseData(data);
      }
    } catch (err) {
      console.error('Failed to fetch case:', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (caseId) {
      fetchCaseData();
    }
  }, [caseId, fetchCaseData]);

  // Seed included_forms from the transaction type on first load if the case
  // hasn't been customised yet. This way new cases inherit a sensible default
  // package without the agent having to tick boxes manually.
  useEffect(() => {
    const c = caseData?.case;
    if (!c) return;
    if ((c.included_forms || []).length > 0) return;
    const defaults = DEFAULT_INCLUDED_BY_TXN[c.transaction_type] || DEFAULT_INCLUDED_BY_TXN.new_coverage;
    if (defaults.length === 0) return;
    // Fire-and-forget — backend will persist and fetchCaseData will refresh.
    fetch(
      `${API_URL}/api/compliance/cases/${caseId}?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ included_forms: defaults }),
      }
    ).then(r => { if (r.ok) fetchCaseData(); }).catch(() => {});
     
  }, [caseData?.case?.id]);

  const updateDocument = async (docType, updates) => {
    try {
      await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/documents/${docType}?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        }
      );
      fetchCaseData();
    } catch (err) {
      console.error('Failed to update document:', err);
    }
  };

  const updateCaseStatus = async (status) => {
    try {
      const response = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/status?status=${status}&advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        { method: 'PUT' }
      );
      if (response.ok) {
        fetchCaseData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Cannot update status');
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  /**
   * Toggle whether a given form is included in the final signed compliance
   * package. Forms not in `included_forms` are skipped during PDF assembly.
   */
  const updateIncludedForms = async (formKey) => {
    if (!formKey || formKey.startsWith('__')) return; // overview / signature tabs
    const current = caseData?.case?.included_forms || [];
    const next = current.includes(formKey)
      ? current.filter(f => f !== formKey)
      : [...current, formKey];
    try {
      const r = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ included_forms: next }),
        }
      );
      if (r.ok) fetchCaseData();
    } catch (err) {
      console.error('Failed to update included_forms:', err);
    }
  };

  // Iter 52.1 — toggle per-section "force new PDF page" flag.
  const updatePageBreakBefore = async (formKey) => {
    if (!formKey || formKey.startsWith('__')) return;
    const current = caseData?.case?.page_break_before || [];
    const next = current.includes(formKey)
      ? current.filter(f => f !== formKey)
      : [...current, formKey];
    try {
      const r = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ page_break_before: next }),
        }
      );
      if (r.ok) fetchCaseData();
    } catch (err) {
      console.error('Failed to update page_break_before:', err);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      not_started: { color: 'bg-gray-100 text-gray-600', icon: Clock },
      in_progress: { color: 'bg-blue-100 text-blue-600', icon: Edit },
      complete: { color: 'bg-green-100 text-green-600', icon: CheckCircle },
      requires_signature: { color: 'bg-yellow-100 text-yellow-600', icon: FileCheck },
      requires_supervisor: { color: 'bg-orange-100 text-orange-600', icon: Shield },
      not_applicable: { color: 'bg-gray-50 text-gray-400', icon: XCircle },
    };
    return configs[status] || configs.not_started;
  };

  const getCaseStatusConfig = (status) => {
    const configs = {
      draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
      intake_pending: { color: 'bg-sky-100 text-sky-700', label: 'Awaiting Client' },
      in_progress: { color: 'bg-blue-100 text-blue-700', label: 'In Progress' },
      pending_client_signature: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending Client Signature' },
      pending_supervisor_review: { color: 'bg-orange-100 text-orange-700', label: 'Pending Supervisor Review' },
      submitted_to_insurer: { color: 'bg-purple-100 text-purple-700', label: 'Submitted to Insurer' },
      underwriting: { color: 'bg-indigo-100 text-indigo-700', label: 'Underwriting' },
      approved: { color: 'bg-teal-100 text-teal-700', label: 'Approved' },
      issued: { color: 'bg-cyan-100 text-cyan-700', label: 'Issued' },
      delivered: { color: 'bg-green-100 text-green-700', label: 'Delivered' },
      closed: { color: 'bg-emerald-100 text-emerald-700', label: 'Closed' },
      compliance_hold: { color: 'bg-red-100 text-red-700', label: 'Compliance Hold' },
    };
    return configs[status] || { color: 'bg-gray-100 text-gray-700', label: status };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#003D5C]"></div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Case not found</p>
        <Button onClick={() => onNavigate('dashboard')} className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const { case: caseInfo, client, documents, fact_find, needs_analysis, quotes, recommendation, replacement, aml_kyc, underwriting, delivery, supervisor_review } = caseData;

  // Stage navigation (Iter 64 — vertical left rail). Quotes stage removed;
  // Investor Profile stage added.
  const applicationTabs = [
    { id: 'overview', label: 'Overview', key: '__overview__' },
    { id: 'advisor-statement', label: 'Advisor Disclosure Statement', key: 'advisor_disclosure' },
    { id: 'privacy-consent', label: 'Privacy Disclosure & Consent', key: 'privacy_consent' },
    { id: 'electronic-consent', label: 'Electronic Communications Consent', key: 'electronic_consent' },
    { id: 'conflict-of-interest', label: 'Conflict of Interest Disclosure', key: 'conflict_disclosure' },
    { id: 'fact-find', label: 'Fact Findings', key: 'fact_find' },
    { id: 'needs-analysis', label: 'Needs Analysis', key: 'needs_analysis' },
    { id: 'investor-profile', label: 'Investor Profile', key: 'investor_profile' },
    { id: 'recommendation', label: 'Recommendation', key: 'recommendation' },
    { id: 'coverage-taken', label: 'Coverage Taken', key: 'coverage_taken' },
    ...(caseInfo.replacement_required ? [{ id: 'replacement', label: 'Replacement', key: 'replacement_rationale' }] : []),
    { id: 'advisor-notes', label: 'Advisor Notes', key: 'advisor_notes' },
  ];
  const postTabs = [
    { id: 'delivery', label: 'Policy Delivery', key: 'policy_delivery' },
    { id: 'reason-why-letter', label: 'Reason Why', key: 'reason_why' },
    { id: 'signatures', label: 'E-Signature', key: '__signature__' },
  ];
  const tabProps = {
    activeSection, setActiveSection,
    includedForms: caseInfo.included_forms || [],
    onToggleInclude: (k) => updateIncludedForms(k),
    pageBreakBefore: caseInfo.page_break_before || [],
    onTogglePageBreak: (k) => updatePageBreakBefore(k),
  };
  
  // Calculate completion percentage
  const requiredDocs = documents?.filter(d => d.required) || [];
  const completeDocs = requiredDocs.filter(d => d.status === 'complete' || d.status === 'not_applicable');
  const completionPercent = requiredDocs.length > 0 ? Math.round((completeDocs.length / requiredDocs.length) * 100) : 0;

  // Group documents by category
  const docCategories = {
    'Client Information': ['contact_record', 'advisor_disclosure', 'privacy_consent', 'electronic_consent'],
    'Analysis & Recommendation': ['fact_find', 'needs_analysis', 'existing_insurance', 'product_illustration', 'recommendation'],
    'Disclosures': ['product_disclosure', 'conflict_disclosure', 'aml_kyc'],
    'Application': ['application_checklist', 'payment_auth', 'beneficiary'],
    'Replacement': ['lird', 'comparative_analysis', 'replacement_rationale'],
    'Approval & Delivery': ['supervisor_approval', 'underwriting_notes', 'policy_delivery', 'client_acknowledgement', 'service_notes']
  };

  const downloadPackage = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/package?advisor_id=${agent?.id || 'admin'}&advisor_name=${encodeURIComponent(agent?.name || 'Admin')}`
      );
      if (!response.ok) {
        alert('Failed to generate package');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance_file_${caseInfo.case_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-2 sm:gap-4 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => onNavigate('cases')} className="flex-shrink-0">
            <ArrowLeft className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1 className="text-lg sm:text-2xl font-bold text-[#003D5C] break-all" data-testid="case-number">{caseInfo.case_number}</h1>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 truncate">{client?.full_name} • {caseInfo.carrier} • ${caseInfo.coverage_amount?.toLocaleString()}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={downloadPackage} className="flex-1 sm:flex-none" data-testid="export-pdf-btn">
            <Download className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Export Compliance File</span>
            <span className="sm:hidden ml-1">Export</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!window.confirm(`Permanently delete file ${caseInfo.case_number}?\n\nThis cannot be undone. All sub-records will be removed.`)) return;
              const advisorId = (isAdmin || agent?.id === 'admin') ? 'admin' : agent.id;
              const advisorName = (isAdmin || agent?.id === 'admin') ? 'Admin' : (agent?.name || 'Advisor');
              const r = await fetch(
                `${API_URL}/api/compliance/cases/${caseId}?advisor_id=${advisorId}&advisor_name=${encodeURIComponent(advisorName)}`,
                { method: 'DELETE' }
              );
              if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                alert(`Delete failed: ${err.detail || r.statusText}`);
                return;
              }
              onNavigate('cases');
            }}
            className="border-red-300 text-red-600 hover:bg-red-50 flex-1 sm:flex-none"
            data-testid="delete-file-btn"
          >
            <Trash2 className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Delete File</span>
            <span className="sm:hidden ml-1">Delete</span>
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-4 lg:gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          {/* Stage navigation — vertical left rail (Iter 64) */}
          <Card data-testid="stage-nav">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Stages</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Application Stage</div>
                <div className="flex flex-col gap-0.5">
                  {applicationTabs.map(tab => (
                    <FormTab key={tab.id} tab={tab} vertical {...tabProps} />
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Post Application</div>
                <div className="flex flex-col gap-0.5">
                  {postTabs.map(tab => (
                    <FormTab key={tab.id} tab={tab} vertical {...tabProps} />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Progress Card */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Completion</span>
                <span className="text-sm font-bold text-[#0077B6]">{completionPercent}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-[#0077B6] h-2 rounded-full transition-all"
                  style={{ width: `${completionPercent}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {completeDocs.length} of {requiredDocs.length} required documents
              </p>
            </CardContent>
          </Card>

          {/* Client Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Client</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-sm">{client?.full_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="text-sm truncate">{client?.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400" />
                <span className="text-sm">{client?.phone}</span>
              </div>
            </CardContent>
          </Card>

          {/* Case Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">File Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Product</span>
                <span className="font-medium">{caseInfo.product_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Carrier</span>
                <span className="font-medium">{caseInfo.carrier}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Coverage</span>
                <span className="font-medium">${caseInfo.coverage_amount?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Premium</span>
                <span className="font-medium">${caseInfo.premium?.toLocaleString()}/{caseInfo.payment_frequency}</span>
              </div>
            </CardContent>
          </Card>

          {/* Flags */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                Compliance Flags
                <span
                  className="text-gray-400 text-xs cursor-help"
                  title="Auto-applied requirements derived from this file. Each flag triggers extra mandatory tabs and disclosures (e.g. AML/KYC, LIRD, Supervisor Approval)."
                  data-testid="compliance-flags-help"
                >ⓘ</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {caseInfo.replacement_required && (
                <div className="flex items-center gap-2 text-yellow-600" title="The proposed policy replaces an existing one. The Replacement (LIRD) tab and Comparative Analysis are required.">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm">Replacement</span>
                </div>
              )}
              {caseInfo.supervision_required && (
                <div className="flex items-center gap-2 text-orange-600" title="A Supervisor must review and sign-off before the file can be submitted to the insurer.">
                  <Shield className="h-4 w-4" />
                  <span className="text-sm">Supervision Required</span>
                </div>
              )}
              {caseInfo.aml_required && (
                <div className="flex items-center gap-2 text-purple-600" title="Anti-Money-Laundering / Know-Your-Client tab is mandatory (FINTRAC reporting threshold).">
                  <FileCheck className="h-4 w-4" />
                  <span className="text-sm">AML/KYC Required</span>
                </div>
              )}
              {caseInfo.is_investment_product && (
                <div className="flex items-center gap-2 text-indigo-600" title="Investment-linked product: additional suitability and KYP (Know-Your-Product) disclosures apply.">
                  <DollarSign className="h-4 w-4" />
                  <span className="text-sm">Investment Product</span>
                </div>
              )}
              {!caseInfo.replacement_required && !caseInfo.supervision_required && 
               !caseInfo.aml_required && !caseInfo.is_investment_product && (
                <div className="flex items-center gap-2 text-green-600" title="No special compliance flags applied — standard insurance file.">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm">Standard Case</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Overview Tab */}
          {activeSection === 'overview' && (
            <div className="space-y-6">
              {/* Self-serve client intake (Iter 32) */}
              <ClientIntakePanel caseId={caseId} agent={agent} onUpdate={fetchCaseData} />

              {/* Uploaded documents — signed paper / scans (Iter 59) */}
              <UploadedDocumentsPanel caseId={caseId} agent={agent} />
            </div>
          )}

          {/* Fact-Find Tab */}
          {activeSection === 'fact-find' && (
            <FactFindForm 
              caseId={caseId} 
              factFind={fact_find} 
              client={client}
              agent={agent}
              onUpdate={fetchCaseData}
            />
          )}

          {/* Needs Analysis Tab */}
          {activeSection === 'needs-analysis' && (
            <NeedsAnalysisForm
              caseId={caseId}
              needsAnalysis={needs_analysis}
              factFind={fact_find}
              caseInfo={caseInfo}
              client={client}
              agent={agent}
              onUpdate={fetchCaseData}
            />
          )}

          {/* Investor Profile Tab (Iter 64) */}
          {activeSection === 'investor-profile' && (
            <InvestorProfileForm caseId={caseId} investorProfile={caseInfo.investor_profile} agent={agent} onUpdate={fetchCaseData} />
          )}

          {/* Coverage Taken Tab (Iter 31) */}
          {activeSection === 'coverage-taken' && (
            <CoverageTakenForm caseId={caseId} agent={agent} client={client} onUpdate={fetchCaseData} />
          )}

          {/* Recommendation Tab */}
          {activeSection === 'recommendation' && (
            <RecommendationForm caseId={caseId} recommendation={recommendation} caseInfo={caseInfo} agent={agent} onUpdate={fetchCaseData} />
          )}

          {/* Replacement Tab */}
          {activeSection === 'replacement' && caseInfo.replacement_required && (
            <ReplacementForm caseId={caseId} replacement={replacement} agent={agent} onUpdate={fetchCaseData} />
          )}

          {/* AML/KYC Tab */}
          {activeSection === 'aml-kyc' && caseInfo.aml_required && (
            <AMLKYCForm caseId={caseId} aml={aml_kyc} agent={agent} onUpdate={fetchCaseData} />
          )}

          {/* Underwriting Tab */}
          {activeSection === 'underwriting' && (
            <UnderwritingNotesForm caseId={caseId} notes={underwriting || []} agent={agent} onUpdate={fetchCaseData} />
          )}

          {/* Policy Delivery Tab */}
          {activeSection === 'delivery' && (
            <PolicyDeliveryForm caseId={caseId} delivery={delivery} caseInfo={caseInfo} agent={agent} onUpdate={fetchCaseData} />
          )}

          {/* Supervisor Tab */}
          {activeSection === 'supervisor' && (
            <SupervisorReviewForm caseId={caseId} review={supervisor_review} agent={agent} onUpdate={fetchCaseData} isAdmin={isAdmin} />
          )}

          {/* E-Signature Tab — single step covering all included application & post-application documents */}
          {activeSection === 'signatures' && (
            <SignatureRequestsForm caseId={caseId} client={client} agent={agent} onUpdate={fetchCaseData} />
          )}

          {/* Advisor Statement — auto-generated in PDF; minimal UI */}
          {activeSection === 'advisor-statement' && (
            <Card data-testid="advisor-statement-card">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-700 mb-3">
                  The Advisor Statement (CLHIA G19 format) is auto-generated using the advisor's profile
                  and carrier affiliations. Update the advisor's <b>My Carriers</b> tab to change the
                  list of authorized carriers shown on this statement.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
                  Advisor: <b>{agent?.name}</b> · Licence <b>{agent?.license_number || '—'}</b>.
                </div>
              </CardContent>
            </Card>
          )}

          {/* Privacy Consent — auto-generated in PDF; minimal UI */}
          {activeSection === 'privacy-consent' && (
            <Card data-testid="privacy-consent-card">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-700">
                  The Privacy Notice & Consent (PIPEDA / CLHIA G3 format) is auto-generated in the final
                  PDF compliance package. Client signs via e-signature step.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Electronic Communication Consent (step 3) */}
          {activeSection === 'electronic-consent' && (
            <ElectronicConsentForm caseId={caseId} agent={agent} onUpdate={fetchCaseData} />
          )}

          {/* Conflict of Interest (step 4) */}
          {activeSection === 'conflict-of-interest' && (
            <ConflictOfInterestForm caseId={caseId} agent={agent} onUpdate={fetchCaseData} />
          )}

          {/* Advisor Notes (step 11) */}
          {activeSection === 'advisor-notes' && (
            <AdvisorNotesForm caseId={caseId} agent={agent} onUpdate={fetchCaseData} />
          )}

          {/* Reason Why Letter (step 16) */}
          {activeSection === 'reason-why-letter' && (
            <ReasonWhyLetterForm caseId={caseId} agent={agent} onUpdate={fetchCaseData} />
          )}

          {/* Documents Tab (now "Compliance Checklist" — step 18) */}
          {activeSection === 'documents' && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>All Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {documents?.map(doc => {
                      const config = getStatusConfig(doc.status);
                      const Icon = config.icon;
                      return (
                        <div 
                          key={doc.document_type}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.color}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-medium">{getDocumentName(doc.document_type)}</p>
                              <p className="text-xs text-gray-500">{doc.required ? 'Required' : 'Optional'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={doc.status}
                              onChange={(e) => updateDocument(doc.document_type, { status: e.target.value })}
                              className="px-2 py-1 border rounded text-sm"
                            >
                              <option value="not_started">Not Started</option>
                              <option value="in_progress">In Progress</option>
                              <option value="complete">Complete</option>
                              <option value="requires_signature">Requires Signature</option>
                              <option value="not_applicable">N/A</option>
                            </select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getDocumentName = (type) => {
  const names = {
    contact_record: 'Client Contact Record',
    advisor_disclosure: 'Advisor Disclosure',
    privacy_consent: 'Privacy Consent',
    electronic_consent: 'Electronic Communication Consent',
    fact_find: 'Fact-Find',
    needs_analysis: 'Needs Analysis',
    existing_insurance: 'Existing Insurance Summary',
    quote_comparison: 'Quote Comparison',
    product_illustration: 'Product Illustration',
    recommendation: 'Recommendation Summary',
    product_disclosure: 'Product Disclosure',
    conflict_disclosure: 'Conflict-of-Interest Disclosure',
    aml_kyc: 'AML/KYC Documents',
    application_checklist: 'Application Checklist',
    payment_auth: 'Payment Authorization',
    beneficiary: 'Beneficiary Designation',
    lird: 'LIRD',
    comparative_analysis: 'Written Comparative Analysis',
    replacement_rationale: 'Replacement Rationale',
    supervisor_approval: 'Supervisor Approval',
    underwriting_notes: 'Underwriting Notes',
    policy_delivery: 'Policy Delivery Receipt',
    client_acknowledgement: 'Client Acknowledgement',
    service_notes: 'Post-Sale Review/Service Notes',
  };
  return names[type] || type;
};

const getStatusLabel = (status) => {
  const labels = {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    complete: 'Complete',
    requires_signature: 'Requires Signature',
    requires_supervisor: 'Requires Supervisor',
    not_applicable: 'Not Applicable',
  };
  return labels[status] || status;
};

// Fact-Find Form Component
const FactFindForm = ({ caseId, factFind, client, agent, onUpdate }) => {
  const [formData, setFormData] = useState(factFind || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setFormData(factFind || {}); }, [factFind]);

  // Iter 64 — auto-save Fact Find every minute (silent, no refetch).
  useAutosave(() => fetch(
    `${API_URL}/api/compliance/cases/${caseId}/fact-find?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
    { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) }
  ), formData);

  const objectives = [
    'Mortgage Protection', 'Income Replacement', 'Final Expenses',
    'Education Funding', 'Estate Planning', 'Business Protection',
    'Tax Planning', 'Wealth Transfer', 'Charitable Giving'
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/fact-find?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        }
      );
      onUpdate();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleObjective = (obj) => {
    const current = formData.objectives || [];
    const updated = current.includes(obj)
      ? current.filter(o => o !== obj)
      : [...current, obj];
    setFormData({ ...formData, objectives: updated });
  };

  // Iter 31: spouse/other-insureds toggle is now driven by the explicit
  // include_other_insureds flag on the client. Falls back to spouse_name
  // for legacy clients created before the flag existed.
  const hasSpouse = !!(client?.include_other_insureds || client?.spouse_name);
  const primaryName = client?.full_name || 'Client';
  const spouseName = client?.spouse_name || 'Spouse';

  const sumOf = (keys) => keys.reduce((acc, k) => acc + (parseFloat(formData[k]) || 0), 0);

  const assetKeys = [
    { key: 'cash_savings', spouseKey: 'spouse_cash_savings', label: 'Cash Savings' },
    { key: 'rrsp', spouseKey: 'spouse_rrsp', label: 'RRSP' },
    { key: 'tfsa', spouseKey: 'spouse_tfsa', label: 'TFSA' },
    { key: 'fhsa', spouseKey: 'spouse_fhsa', label: 'FHSA' },
    { key: 'non_registered', spouseKey: 'spouse_non_registered', label: 'Non-Registered Investments' },
    { key: 'real_estate_value', spouseKey: 'spouse_real_estate_value', label: 'Real Estate Value' },
    { key: 'business_assets', spouseKey: 'spouse_business_assets', label: 'Business Assets' },
    { key: 'other_assets', spouseKey: 'spouse_other_assets', label: 'Other Assets' },
  ];
  const liabilityKeys = [
    { key: 'mortgage_balance', spouseKey: 'spouse_mortgage_balance', label: 'Mortgage Balance' },
    { key: 'credit_cards', spouseKey: 'spouse_credit_cards', label: 'Credit Cards' },
    { key: 'loans', spouseKey: 'spouse_loans', label: 'Loans' },
    { key: 'line_of_credit', spouseKey: 'spouse_line_of_credit', label: 'Line of Credit' },
    { key: 'business_debt', spouseKey: 'spouse_business_debt', label: 'Business Debt' },
    { key: 'other_liabilities', spouseKey: 'spouse_other_liabilities', label: 'Other Liabilities' },
  ];
  const insuranceKeys = [
    { key: 'group_life', spouseKey: 'spouse_group_life', label: 'Group Life' },
    { key: 'individual_life', spouseKey: 'spouse_individual_life', label: 'Individual Life' },
    { key: 'mortgage_insurance', spouseKey: 'spouse_mortgage_insurance', label: 'Mortgage Insurance' },
    { key: 'critical_illness', spouseKey: 'spouse_critical_illness', label: 'Critical Illness' },
    { key: 'disability_insurance', spouseKey: 'spouse_disability_insurance', label: 'Disability Insurance' },
    { key: 'creditor_insurance', spouseKey: 'spouse_creditor_insurance', label: 'Creditor Insurance' },
  ];

  const renderSection = (title, fields, totalKeys, dataTestPrefix, totalLabel) => {
    const clientTotal = sumOf(totalKeys.map(k => k.key));
    const spouseTotal = sumOf(totalKeys.map(k => k.spouseKey));
    return (
      <div data-testid={`${dataTestPrefix}-section`}>
        <h3 className="font-semibold mb-3 pb-2 border-b">{title}</h3>
        <div className={`grid ${hasSpouse ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2'} gap-2 mb-2 text-xs font-semibold text-gray-500`}>
          <div className="hidden sm:block"></div>
          <div className="text-right">{primaryName}</div>
          {hasSpouse && <div className="text-right hidden sm:block">{spouseName}</div>}
        </div>
        <div className="space-y-2">
          {fields.map(({ key, spouseKey, label }) => (
            <div key={key} className={`grid ${hasSpouse ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2'} gap-2 items-center`}>
              <label className="text-sm font-medium">{label}</label>
              <input
                type="number"
                min="0"
                value={formData[key] || ''}
                onChange={(e) => setFormData({ ...formData, [key]: parseFloat(e.target.value) || 0 })}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6] text-right"
                placeholder={`${primaryName.split(' ')[0]} - $0`}
                data-testid={`ff-${key}`}
              />
              {hasSpouse && (
                <input
                  type="number"
                  min="0"
                  value={formData[spouseKey] || ''}
                  onChange={(e) => setFormData({ ...formData, [spouseKey]: parseFloat(e.target.value) || 0 })}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6] text-right"
                  placeholder={`${spouseName.split(' ')[0]} - $0`}
                  data-testid={`ff-${spouseKey}`}
                />
              )}
            </div>
          ))}
          {/* Subtotal row */}
          <div className={`grid ${hasSpouse ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2'} gap-2 items-center pt-2 border-t font-semibold`} data-testid={`${dataTestPrefix}-subtotal`}>
            <div>{totalLabel}</div>
            <div className="text-right">${clientTotal.toLocaleString()}</div>
            {hasSpouse && <div className="text-right">${spouseTotal.toLocaleString()}</div>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Fact Findings Form</CardTitle>
          <Button onClick={handleSave} disabled={saving} className="bg-[#0077B6] hover:bg-[#005A8C]" data-testid="save-fact-find-btn">
            {saving ? 'Saving...' : <><Save className="h-4 w-4 mr-2" />Save</>}
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Objectives — at TOP per UX request */}
          <div data-testid="ff-objectives-section">
            <h3 className="font-semibold mb-3 pb-2 border-b">Insurance Objectives (Client Goals)</h3>
            <div className="flex flex-wrap gap-2">
              {objectives.map(obj => (
                <button
                  key={obj}
                  type="button"
                  onClick={() => toggleObjective(obj)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    (formData.objectives || []).includes(obj)
                      ? 'bg-[#0077B6] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`ff-obj-${obj}`}
                >
                  {obj}
                </button>
              ))}
            </div>
            <textarea
              value={formData.objective_notes || ''}
              onChange={(e) => setFormData({ ...formData, objective_notes: e.target.value })}
              placeholder="Additional notes about the client's goals (optional)..."
              rows="2"
              className="w-full mt-3 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
              data-testid="ff-objective-notes"
            />
          </div>

          {hasSpouse && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
              Side-by-side household view enabled. Spouse: <b>{spouseName}</b>.
            </div>
          )}

          {renderSection('Assets', assetKeys, assetKeys, 'ff-assets', 'Total Assets')}
          {renderSection('Liabilities', liabilityKeys, liabilityKeys, 'ff-liabilities', 'Total Liabilities')}
          {renderSection('Existing Insurance Coverage', insuranceKeys, insuranceKeys, 'ff-insurance', 'Total Existing Coverage')}

          {/* Household Budget */}
          <div>
            <h3 className="font-semibold mb-3 pb-2 border-b">Household Budget</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Desired Monthly Premium</label>
                <input
                  type="number"
                  min="0"
                  value={formData.desired_monthly_premium || ''}
                  onChange={(e) => setFormData({ ...formData, desired_monthly_premium: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  placeholder="$0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Maximum Affordable Premium</label>
                <input
                  type="number"
                  min="0"
                  value={formData.maximum_affordable_premium || ''}
                  onChange={(e) => setFormData({ ...formData, maximum_affordable_premium: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  placeholder="$0"
                />
              </div>
            </div>
          </div>

          {/* Existing Policies (optional sub-table, Iter 31) */}
          <ExistingPoliciesSubTable
            policies={formData.existing_policies || []}
            hasSpouse={hasSpouse}
            onChange={(next) => setFormData({ ...formData, existing_policies: next })}
          />

          {/* Advisor Reasoning — at BOTTOM */}
          <div>
            <h3 className="font-semibold mb-3 pb-2 border-b">Advisor's Reason / Notes</h3>
            <textarea
              rows="4"
              value={formData.advisor_reasoning || ''}
              onChange={(e) => setFormData({ ...formData, advisor_reasoning: e.target.value })}
              placeholder="Advisor's reasoning, context, observations from the discovery meeting..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
              data-testid="ff-advisor-reasoning"
            />
          </div>

          {/* Mark Complete */}
          <div className="flex items-center gap-2 pt-4 border-t">
            <input
              type="checkbox"
              id="fact-find-complete"
              checked={formData.completed || false}
              onChange={(e) => setFormData({ ...formData, completed: e.target.checked })}
              className="w-4 h-4 text-[#0077B6] rounded"
              data-testid="ff-complete"
            />
            <label htmlFor="fact-find-complete" className="text-sm font-medium">
              Mark Fact Findings as Complete
            </label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Needs Analysis Form Component
const NeedsAnalysisForm = ({ caseId, needsAnalysis, factFind, caseInfo, client, agent, onUpdate }) => {
  const [formData, setFormData] = useState(needsAnalysis || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setFormData(needsAnalysis || {}); }, [needsAnalysis]);

  // Auto-prime from fact-find (client + spouse) on first load
  useEffect(() => {
    if (factFind && !needsAnalysis) {
      const mortgageDebt = (factFind.mortgage_balance || 0);
      const otherDebt = (factFind.credit_cards || 0) + (factFind.loans || 0) +
                        (factFind.line_of_credit || 0) + (factFind.business_debt || 0) +
                        (factFind.other_liabilities || 0);
      const existingCoverage = (factFind.group_life || 0) + (factFind.individual_life || 0) +
                               (factFind.mortgage_insurance || 0);
      const availableAssets = (factFind.cash_savings || 0) + (factFind.rrsp || 0) +
                              (factFind.tfsa || 0) + (factFind.non_registered || 0);

      const sMortgageDebt = (factFind.spouse_mortgage_balance || 0);
      const sOtherDebt = (factFind.spouse_credit_cards || 0) + (factFind.spouse_loans || 0) +
                         (factFind.spouse_line_of_credit || 0) + (factFind.spouse_business_debt || 0) +
                         (factFind.spouse_other_liabilities || 0);
      const sExistingCoverage = (factFind.spouse_group_life || 0) + (factFind.spouse_individual_life || 0) +
                                (factFind.spouse_mortgage_insurance || 0);
      const sAvailableAssets = (factFind.spouse_cash_savings || 0) + (factFind.spouse_rrsp || 0) +
                               (factFind.spouse_tfsa || 0) + (factFind.spouse_non_registered || 0);

      setFormData(prev => ({
        ...prev,
        mortgage_debt_need: mortgageDebt,
        other_debt_need: otherDebt,
        existing_coverage_offset: existingCoverage,
        available_assets_offset: availableAssets,
        spouse_mortgage_debt_need: sMortgageDebt,
        spouse_other_debt_need: sOtherDebt,
        spouse_existing_coverage_offset: sExistingCoverage,
        spouse_available_assets_offset: sAvailableAssets,
      }));
    }
  }, [factFind, needsAnalysis]);

  const num = (k) => parseFloat(formData[k]) || 0;
  // Iter 64 — auto-save Needs Analysis every minute (silent).
  useAutosave(() => fetch(
    `${API_URL}/api/compliance/cases/${caseId}/needs-analysis?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
    { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) }
  ), formData);
  // Iter 31: spouse/other-insureds toggle is now driven by the explicit
  // include_other_insureds flag on the client. Falls back to spouse_name
  // for legacy clients created before the flag existed.
  const hasSpouse = !!(client?.include_other_insureds || client?.spouse_name);
  const primaryName = client?.full_name || 'Client';
  const spouseName = client?.spouse_name || 'Spouse';

  const clientNeedKeys = ['mortgage_debt_need', 'other_debt_need', 'final_expense_need', 'income_replacement_need', 'education_fund_need', 'emergency_fund_need'];
  const spouseNeedKeys = ['spouse_mortgage_debt_need', 'spouse_other_debt_need', 'spouse_final_expense_need', 'spouse_income_replacement_need', 'spouse_education_fund_need', 'spouse_emergency_fund_need'];

  const clientTotal = clientNeedKeys.reduce((a, k) => a + num(k), 0) - num('existing_coverage_offset') - num('available_assets_offset');
  const spouseTotal = spouseNeedKeys.reduce((a, k) => a + num(k), 0) - num('spouse_existing_coverage_offset') - num('spouse_available_assets_offset');
  const householdTotal = clientTotal + spouseTotal;

  const recommended = num('recommended_coverage') || (caseInfo?.coverage_amount || 0);
  const shortfall = clientTotal - recommended;

  const needRows = [
    { key: 'mortgage_debt_need', spouseKey: 'spouse_mortgage_debt_need', label: 'Mortgage / Debt Protection' },
    { key: 'other_debt_need', spouseKey: 'spouse_other_debt_need', label: 'Other Debt Protection' },
    { key: 'final_expense_need', spouseKey: 'spouse_final_expense_need', label: 'Final Expenses' },
    { key: 'income_replacement_need', spouseKey: 'spouse_income_replacement_need', label: `Income Replacement (${formData.income_replacement_years || 10} yrs)` },
    { key: 'education_fund_need', spouseKey: 'spouse_education_fund_need', label: 'Education Fund' },
    { key: 'emergency_fund_need', spouseKey: 'spouse_emergency_fund_need', label: 'Emergency Fund' },
  ];

  const offsetRows = [
    { key: 'existing_coverage_offset', spouseKey: 'spouse_existing_coverage_offset', label: 'Existing Coverage Offset' },
    { key: 'available_assets_offset', spouseKey: 'spouse_available_assets_offset', label: 'Available Assets Offset' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/needs-analysis?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            total_calculated_need: clientTotal,
            spouse_total_calculated_need: spouseTotal,
            household_total_need: householdTotal,
            coverage_shortfall: shortfall,
          }),
        }
      );
      onUpdate();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (row, isOffset = false) => (
    <div key={row.key} className={`grid ${hasSpouse ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'} gap-2 items-center`}>
      <label className="text-sm font-medium col-span-2 sm:col-span-1">{row.label}</label>
      <input
        type="number"
        min="0"
        value={formData[row.key] || ''}
        onChange={(e) => setFormData({ ...formData, [row.key]: parseFloat(e.target.value) || 0 })}
        className="px-2 py-2 border rounded text-right"
        placeholder={`${primaryName.split(' ')[0]} $0`}
        data-testid={`na-${row.key}`}
      />
      {hasSpouse && (
        <input
          type="number"
          min="0"
          value={formData[row.spouseKey] || ''}
          onChange={(e) => setFormData({ ...formData, [row.spouseKey]: parseFloat(e.target.value) || 0 })}
          className="px-2 py-2 border rounded text-right"
          placeholder={`${spouseName.split(' ')[0]} $0`}
          data-testid={`na-${row.spouseKey}`}
        />
      )}
      <div className="text-right text-sm text-gray-600 font-medium col-span-2 sm:col-span-1">
        <span className="sm:hidden text-xs text-gray-400">Combined: </span>
        ${(num(row.key) + num(row.spouseKey)).toLocaleString()}{isOffset ? ' (-)' : ''}
      </div>
    </div>
  );

  // AI generation hook (defined inside the component so we can mutate formData)
  const [aiGen, setAiGen] = useState(false);
  const generateWithAI = async () => {
    setAiGen(true);
    try {
      const r = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/ai-needs-analysis?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'AI failed');
      }
      const j = await r.json();
      setFormData(prev => ({ ...prev, ...j.fields }));
      onUpdate && onUpdate();
      alert('AI-suggested values populated and saved. Review & adjust as needed.');
    } catch (e) {
      alert('AI generation failed: ' + e.message);
    } finally { setAiGen(false); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Needs Analysis
          </CardTitle>
          <div className="flex gap-2">
            <Button onClick={generateWithAI} disabled={aiGen} variant="outline" className="border-[#D4AF37] text-[#003D5C]" data-testid="ai-needs-btn">
              {aiGen ? 'Generating...' : <>✨ Generate with AI</>}
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0077B6] hover:bg-[#005A8C]" data-testid="save-needs-btn">
              {saving ? 'Saving...' : <><Save className="h-4 w-4 mr-2" />Save</>}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {hasSpouse && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
              Side-by-side household needs analysis. Combined household total used for protection planning.
            </div>
          )}
          {/* Header row */}
          <div className={`grid ${hasSpouse ? 'grid-cols-4' : 'grid-cols-3'} gap-2 text-xs font-semibold text-gray-500 pb-1 border-b`}>
            <div></div>
            <div className="text-right">{primaryName}</div>
            {hasSpouse && <div className="text-right">{spouseName}</div>}
            <div className="text-right">Household</div>
          </div>

          {/* Needs */}
          <div>
            <h3 className="font-semibold mb-2 text-green-700">Insurance Needs (+)</h3>
            <div className="space-y-2">{needRows.map(r => renderRow(r))}</div>
          </div>

          {/* Offsets */}
          <div>
            <h3 className="font-semibold mb-2 text-red-700">Offsets (-)</h3>
            <div className="space-y-2">{offsetRows.map(r => renderRow(r, true))}</div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-3">Calculation Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Net Need — {primaryName}:</span>
                <span className="font-bold text-[#003D5C]" data-testid="na-client-total">${clientTotal.toLocaleString()}</span>
              </div>
              {hasSpouse && (
                <div className="flex justify-between">
                  <span>Net Need — {spouseName}:</span>
                  <span className="font-bold text-[#003D5C]" data-testid="na-spouse-total">${spouseTotal.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-lg pt-2 border-t">
                <span className="font-semibold">Household Total Need:</span>
                <span className="font-extrabold text-[#003D5C]" data-testid="na-household-total">${householdTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Recommended Coverage:</span>
                <input
                  type="number"
                  min="0"
                  value={formData.recommended_coverage || caseInfo?.coverage_amount || ''}
                  onChange={(e) => setFormData({ ...formData, recommended_coverage: parseFloat(e.target.value) || 0 })}
                  className="w-40 px-2 py-1 border rounded text-right font-medium"
                  data-testid="na-recommended"
                />
              </div>
              <div
                className={`flex justify-between font-bold rounded p-2 ${shortfall > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}
                data-testid="na-surplus-shortfall"
              >
                <span>{shortfall > 0 ? 'Coverage Shortfall:' : 'Coverage Surplus:'}</span>
                <span>${Math.abs(shortfall).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Override */}
          {shortfall !== 0 && (
            <div className="border-t pt-4">
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="advisor-override"
                  checked={formData.advisor_override || false}
                  onChange={(e) => setFormData({ ...formData, advisor_override: e.target.checked })}
                  className="w-4 h-4 text-[#0077B6] rounded mt-1"
                />
                <div className="flex-1">
                  <label htmlFor="advisor-override" className="text-sm font-medium">
                    Advisor Override — Recommended coverage differs from calculated need
                  </label>
                  {formData.advisor_override && (
                    <textarea
                      value={formData.override_reason || ''}
                      onChange={(e) => setFormData({ ...formData, override_reason: e.target.value })}
                      placeholder="Explain why the recommended coverage differs from the calculated need..."
                      rows="3"
                      className="w-full mt-2 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Mark Complete */}
          <div className="flex items-center gap-2 pt-4 border-t">
            <input
              type="checkbox"
              id="needs-complete"
              checked={formData.completed || false}
              onChange={(e) => setFormData({ ...formData, completed: e.target.checked })}
              className="w-4 h-4 text-[#0077B6] rounded"
              data-testid="na-complete"
            />
            <label htmlFor="needs-complete" className="text-sm font-medium">
              Mark Needs Analysis as Complete
            </label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CaseDetail;
